import { TenantLocale, unityEditorUrlFor, unityOriginFor, type EnvName, type TenantConfig, type TenantEnvironment } from "@arc/tenant-config";

/**
 * The tenant identity the shell needs BEFORE React mounts — branding must be in the initial HTML, never
 * JS-injected after boot (theming.md: that is what causes FOUC and a wrong-brand first paint).
 *
 * The edge resolves host → tenant and stamps this onto the document; the app only *reads* it. It is deliberately
 * NOT security state: it drives branding only. Tenancy for data access comes from the verified token `iss`.
 */
export interface TenantBootstrap {
  realm: string;
  productName: string;
  /** CLIENT/organization name (e.g. "Takeda") — distinct from the white-label `productName`. Footer uses this. */
  clientName: string;
  /** Keycloak realm + client for the (later) auth wiring — read from config, never from the URL. */
  clientId: string;
  idpHint?: string;
  /**
   * The tenant's own language — the LAST step of the locale chain (ADR-047 D2), reached only when the browser asks
   * for a language the shell does not ship.
   *
   * **Optional here although the schema REQUIRES it**, and deliberately so. This attribute is baked into the HTML
   * at build time, so a cached document from before ADR-047 simply lacks the key. Failing the whole bootstrap
   * closed over a missing LOCALE would show the "unknown tenant" screen to someone whose branding and tenancy are
   * both perfectly fine — a far worse outcome than the thing it guards. Absent or malformed degrades to the
   * pre-ADR-047 behaviour (`en`), so the worst case is exactly what ships today.
   */
  defaultLocale?: TenantLocale;
  /**
   * The Unity editor's origin — the ONLY peer the embedded panel may exchange `postMessage` with (ADR-049 D1).
   *
   * Stamped by the EDGE rather than read from a bundle constant, for the same reason the tenant is: the edge is
   * what knows the environment, and a second source of truth for "which origin may talk to us" is exactly the
   * kind of thing that drifts. **Absent means the bridge does not arm** — local has no Unity build pointed at
   * it, and an absent peer must fail closed rather than fall back to something permissive.
   */
  unityOrigin?: string;
  /**
   * Where the Unity 3D EDITOR build lives (ADR-051 D3) — the thing we navigate an author to, as opposed to
   * `unityOrigin`, which is who may postMessage the embedded panel. Stamped by the edge for the same reason: it
   * is the edge that knows the environment, and the app should not carry an `env` it could branch on elsewhere.
   *
   * **Absent means no launch.** No Unity build points at a developer's machine, so locally the control is
   * disabled with a reason rather than pointed at a URL that 404s.
   */
  unityEditorUrl?: string;
}

/** Attribute the edge writes onto <html>; the payload is JSON so it survives a strict CSP with no inline script. */
export const BOOTSTRAP_ATTR = "data-arc-tenant";

/** Serialize for injection into the initial HTML. Kept next to the parser so the two can never drift. */

/**
 * Which origin may `postMessage` the embedded panel (ADR-049 D1).
 *
 * Two rules, and both matter:
 *  1. **A configured peer always wins.** The dev override can only fill a hole, never replace a real value — so
 *     if `local` ever gains a genuine Unity origin, it takes precedence automatically.
 *  2. **The override is confined to `local`.** Anywhere else it is discarded outright, because this field is an
 *     origin allow-list and a caller-supplied one reaching a deployed build would be exactly the hole the check
 *     exists to close.
 *
 * Extracted as a pure function on purpose: rule 1 is unobservable through `serializeBootstrap` today, because
 * `unityOriginFor("local")` is null and both orderings agree. A test written against the composed path passed
 * whichever way the precedence ran — vacuous. Here it can be handed a configured value and made to bite.
 */
export function resolveUnityOrigin(configured: string | null, envName: EnvName, devOverride?: string): string | null {
  if (configured) return configured;
  return envName === "local" ? (devOverride ?? null) : null;
}

export function serializeBootstrap(
  t: TenantConfig,
  env: TenantEnvironment,
  envName: EnvName,
  /**
   * DEV ONLY — the origin to treat as the Unity peer, so the bridge harness can actually drive the bridge.
   *
   * **Ignored unless `envName === "local"`**, and that guard is the whole safety story: this field decides WHO
   * may `postMessage` the embedded panel (ADR-049 D1), so a caller-supplied value reaching a deployed build would
   * be an origin allow-list handed to whoever asked. The build emitter never passes it; only the dev middleware
   * does, with the request's own host.
   *
   * Why it is needed at all: `unityOriginFor("local")` is deliberately null — no Unity build points at a
   * developer's machine — so the panel armed no listener and the harness's messages were silently dropped. The
   * harness exists precisely to make that channel observable (ADR-049 D7), and it could not.
   */
  devUnityOrigin?: string,
): string {
  const payload: TenantBootstrap = {
    realm: t.realm,
    productName: t.productName,
    clientName: t.clientName,
    clientId: t.clientId,
    defaultLocale: t.defaultLocale,
    // Per-environment: local has federation disabled, so no hint is sent and Keycloak shows its own login form.
    ...(env.idpHint ? { idpHint: env.idpHint } : {}),
    ...((): { unityOrigin?: string } => {
      const origin = resolveUnityOrigin(unityOriginFor(envName), envName, devUnityOrigin);
      return origin ? { unityOrigin: origin } : {};
    })(),
    ...((): { unityEditorUrl?: string } => {
      const url = unityEditorUrlFor(envName);
      return url ? { unityEditorUrl: url } : {};
    })(),
  };
  return JSON.stringify(payload);
}

/**
 * Read the bootstrap the edge stamped on <html>. Returns null when absent — callers must FAIL CLOSED (render an
 * error, never guess a tenant), mirroring the edge's own unknown-host behaviour.
 */
/**
 * An absolute origin and nothing else — scheme + host (+ port). Returns `undefined` for anything that carries a
 * path, query or fragment, for a relative value, or for a non-http(s) scheme.
 *
 * Strict because the result is compared against `MessageEvent.origin`, which is ALWAYS a bare origin: a value
 * with a trailing slash or a path would never match, and the bridge would fail silently rather than loudly.
 * Fail-closed: anything unrecognised is dropped, and an absent peer does not arm the bridge.
 */
function parsedOrigin(raw: unknown): string | undefined {
  if (typeof raw !== "string" || raw === "") return undefined;
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" && u.protocol !== "http:") return undefined;
    return u.origin === raw ? raw : undefined;
  } catch {
    return undefined;
  }
}

export function readBootstrap(doc: Document = document): TenantBootstrap | null {
  const raw = doc.documentElement.getAttribute(BOOTSTRAP_ATTR);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const t = parsed as Partial<TenantBootstrap>;
    if (typeof t.realm !== "string" || !t.realm) return null;
    if (typeof t.productName !== "string" || !t.productName) return null;
    if (typeof t.clientName !== "string" || !t.clientName) return null;
    if (typeof t.clientId !== "string" || !t.clientId) return null;
    return {
      realm: t.realm,
      productName: t.productName,
      clientName: t.clientName,
      clientId: t.clientId,
      idpHint: t.idpHint,
      // VALIDATED, not trusted: `<html lang>` is set from this, and an unrecognised value would be handed to a
      // screen reader as a language tag. Anything the shell does not ship is dropped rather than rejected — see
      // the field's doc for why a bad locale must not fail the whole bootstrap.
      defaultLocale: parsedLocale(t.defaultLocale),
      // VALIDATED for the same reason, and harder: this value decides WHO may postMessage the embedded panel
      // (ADR-049 D1). The edge writes it, but a reader that trusts the shape would turn a typo into either a
      // dead bridge or — with a path or a wildcard slipped in — a comparison that never means what it reads.
      unityOrigin: parsedOrigin(t.unityOrigin),
      // NOT `parsedOrigin` — this one is a full URL with a path, and an origin-only check would reject every
      // real value. Validated as an absolute http(s) URL instead; anything else is dropped and the launch stays
      // disabled, which is the fail-closed direction.
      unityEditorUrl: parsedAbsoluteUrl(t.unityEditorUrl),
    };
  } catch {
    return null;
  }
}

/** An absolute http(s) URL, path and all. Anything else ⇒ undefined, so a malformed value disables the launch. */
function parsedAbsoluteUrl(raw: unknown): string | undefined {
  if (typeof raw !== "string" || raw === "") return undefined;
  try {
    const u = new URL(raw);
    return u.protocol === "https:" || u.protocol === "http:" ? raw : undefined;
  } catch {
    return undefined;
  }
}

function parsedLocale(v: unknown): TenantLocale | undefined {
  const r = TenantLocale.safeParse(v);
  return r.success ? r.data : undefined;
}

/**
 * The per-tenant CSS custom properties. Only these vary per tenant — everything else is a shared semantic token
 * (see design-system-brief.md). `--brand-interactive` is the WCAG-AA-safe partner and is what text/icons/buttons
 * must use; `--brand` is display-only and may legitimately fail AA for small text.
 */
export function tenantCssVars(t: TenantConfig): string {
  const p = t.brand.palette;
  // The dark-surface SOLID `--primary` fill. Default = the lightened accent (`primaryInteractiveDark`) so the button
  // stays visible on dark; a tenant may opt into its saturated brand (Takeda's `#ed0027`) to keep the white-on-brand
  // identity. `--brand-interactive` (text/icons/focus-ring) is unaffected — it always uses the lightened accent on dark.
  const primaryFillDark = t.brand.primaryFillDark ?? t.brand.primaryInteractiveDark;
  const primaryFill = t.brand.primaryFill ?? t.brand.primaryInteractive;
  return [
    `--brand: ${p[0]}`,
    `--brand-interactive: ${t.brand.primaryInteractive}`,
    `--brand-interactive-dark: ${t.brand.primaryInteractiveDark}`,
    `--brand-2: ${p[1]}`,
    `--brand-3: ${p[2]}`,
    `--brand-4: ${p[3]}`,
    // shadcn's `--primary` pair. The INK IS DERIVED, never a literal: `--primary` differs per tenant AND per
    // theme, and white ink — correct on every tenant's light brand — is 2.44–2.49:1 on the dark-validated brand,
    // i.e. a dark-theme AA failure on all five. `readableInk` picks whichever of the two clears 4.5 by measurement.
    ...(t.brand.primaryFill ? [`--primary: ${t.brand.primaryFill}`] : []),
    `--primary-foreground: ${readableInk(primaryFill)}`,
    // `--primary-dark` = the dark-surface fill (light block ignores it); its ink is derived from the SAME value, so a
    // saturated-brand fill takes white (Takeda 4.54:1) and a lightened fill takes dark ink — AA holds either way.
    `--primary-dark: ${primaryFillDark}`,
    `--primary-foreground-dark: ${readableInk(primaryFillDark)}`,
    ...(t.brand.accentFill ? [`--orange: ${t.brand.accentFill}`, `--on-orange: ${readableInk(t.brand.accentFill)}`] : []),
  ].join("; ");
}

/** The two inks the token layer ships. Any surface must be legible against one of them. */
const INK_LIGHT = "#ffffff";
const INK_DARK = "#2e3c47";

/**
 * The higher-contrast of the two inks for a given background — computed, not chosen.
 *
 * WCAG relative luminance (sRGB), the same formula the contrast tests use. Kept here rather than imported from
 * `@arc/tenant-config` because that package is browser-safe and deliberately dependency-free; duplicating ~6 lines
 * is cheaper than widening its surface, and `token-contrast.test.ts` asserts the RESULT against every tenant, so
 * the two cannot silently disagree.
 */
export function readableInk(background: string): string {
  return contrast(background, INK_LIGHT) >= contrast(background, INK_DARK) ? INK_LIGHT : INK_DARK;
}

function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const chan = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => chan(v / 255)) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
